import { getRequestHeaders, handleRes, logError } from 'utils';
import {
  Entity,
  EntityTypes,
  FileMetadata,
  FileUpload,
  DigitalSignature,
  CreateDigitalSignatureRequest,
} from 'types';

export interface GetFileResponse extends Response {
  file?: FileMetadata;
}

export interface PostFileResponse extends Response {
  files?: Array<FileMetadata>;
}

export const getS3Urls = async (
  files: Array<FileUpload>,
): Promise<PostFileResponse> => {
  const metadataArray = files.map((f) => f.metadata);
  return await fetch(`${window.FILE_BASE_URL}/file/`, {
    method: 'POST',
    body: JSON.stringify(metadataArray),
    ...getRequestHeaders(),
  });
};

export const uploadFile = async (file: FileUpload): Promise<Response> => {
  if (!file.metadata.uploadFileURL) throw new Error('No upload URL found'); // should never happen
  return await fetch(file.metadata.uploadFileURL, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
};

export const getFileByFileKey = async (
  fileKey: string,
): Promise<GetFileResponse> => {
  return await fetch(`${window.FILE_BASE_URL}/file/${fileKey}`, {
    method: 'GET',
    ...getRequestHeaders(),
  });
};

export const getFilesByEntityID = (
  entity: EntityTypes,
  id: number | string | undefined,
): Promise<FileMetadata[]> => {
  return fetch(`${window.FILE_BASE_URL}/file/${entity}/${id}`, {
    method: 'GET',
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<FileMetadata[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const deleteFile = async (fileKey: string): Promise<Response> => {
  return await fetch(`${window.FILE_BASE_URL}/file/${fileKey}`, {
    method: 'DELETE',
    ...getRequestHeaders(),
  });
};

// Digital Signature APIs
export const getDigitalSignaturesForEntity = (
  entity: EntityTypes,
  entityID: number | string,
): Promise<DigitalSignature[]> => {
  return fetch(
    `${window.FILE_BASE_URL}/file/signatures/${entity}/${entityID}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<DigitalSignature[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const createDigitalSignature = async (
  entity: EntityTypes,
  entityID: number | string,
  signature: CreateDigitalSignatureRequest,
): Promise<DigitalSignature> => {
  return fetch(
    `${window.FILE_BASE_URL}/file/signatures/${entity}/${entityID}`,
    {
      method: 'POST',
      body: JSON.stringify(signature),
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<DigitalSignature>(res, 200))
    .catch((e) => {
      logError(e);
      throw e;
    });
};

export const saveUserSignature = async (file: File): Promise<Response> => {
  const headers = getRequestHeaders().headers;
  return await fetch(`${window.FILE_BASE_URL}/file/signature`, {
    method: 'POST',
    body: file,
    headers: {
      ...headers,
      'Content-Type': 'image/png',
    },
  });
};

export const deleteFileByKey = async (fileKey: string): Promise<Response> => {
  return await fetch(`${window.FILE_BASE_URL}/file/${fileKey}`, {
    method: 'DELETE',
    ...getRequestHeaders(),
  });
};
